import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Megaphone, ArrowLeft, AlertCircle, Construction } from "lucide-react";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * ClientCampaignsPage — placeholder for the marketing-campaigns workspace at
 *   /{slug}/campaigns
 * Will be wired to Brevo Email Campaigns + WhatsApp Broadcasts in a follow-up.
 */
export default function ClientCampaignsPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [company, setCompany] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/companies/by-slug/${slug}`);
        if (alive) setCompany(data);
      } catch (e) {
        if (alive) setError(
          e.response?.status === 403 ? "אין לך הרשאה" : (e.response?.data?.detail || "החברה לא נמצאה")
        );
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  useEffect(() => {
    if (!user || !company) return;
    if (user.role === "owner" && user.company_id !== company.id) {
      navigate("/login", { replace: true });
    }
  }, [user, company, navigate]);

  if (error) return (
    <div className="min-h-screen flex items-center justify-center px-4" dir="rtl">
      <div className="text-center space-y-3">
        <AlertCircle className="h-12 w-12 text-rose-500 mx-auto" />
        <p className="font-bold text-lg">{error}</p>
        <Link to="/login"><Button variant="outline">לכניסה</Button></Link>
      </div>
    </div>
  );

  if (!company) return (
    <div className="min-h-screen flex items-center justify-center" dir="rtl">
      <p className="text-stone-500">טוען...</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-fuchsia-50/20" dir="rtl">
      {/* Slim white-label header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate(`/${slug}`)}
            className="flex items-center gap-3 min-w-0 group"
            data-testid="client-back-home"
          >
            {company.logo_url ? (
              <div className="h-10 w-10 rounded-xl bg-white border border-stone-200 overflow-hidden flex items-center justify-center shadow-sm shrink-0 group-hover:scale-105 transition">
                <img src={company.logo_url} alt={company.name} className="h-full w-full object-contain p-0.5" />
              </div>
            ) : (
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-fuchsia-500 to-emerald-500 flex items-center justify-center text-white font-bold text-lg shrink-0">
                {(company.name || "?").trim().charAt(0).toUpperCase()}
              </div>
            )}
            <div className="text-right">
              <h1 className="font-heading font-bold text-base sm:text-lg truncate">{company.display_name || company.name}</h1>
              <p className="text-[10px] text-stone-500 truncate">לחצי לחזרה לדשבורד</p>
            </div>
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div className="bg-white rounded-3xl border-2 border-fuchsia-200 p-8 sm:p-12 text-center shadow-sm">
          <div className="h-16 w-16 mx-auto rounded-2xl bg-gradient-to-br from-fuchsia-500 to-purple-600 flex items-center justify-center mb-5 shadow-lg">
            <Megaphone className="h-8 w-8 text-white" />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-stone-900 mb-2">
            דיוור קמפיינים
          </h1>
          <p className="text-stone-600 mb-6 leading-relaxed max-w-lg mx-auto">
            שליחת הודעות שיווקיות בוואטסאפ ובמייל לכל מאגר הלקוחות שלך — בלחיצה אחת.
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 max-w-md mx-auto mb-6">
            <Construction className="h-6 w-6 text-amber-600 mx-auto mb-2" />
            <p className="font-bold text-amber-900 mb-1">בבנייה — מגיע בקרוב!</p>
            <p className="text-sm text-amber-800 leading-relaxed">
              אנחנו עובדים על המודול הזה. בקרוב תוכלי לשלוח קמפיינים שיווקיים מותאמים אישית לכל מאגר הלקוחות שלך.
            </p>
          </div>

          <Button asChild variant="outline" data-testid="campaigns-back">
            <Link to={`/${slug}`}>
              <ArrowLeft className="h-4 w-4 ml-2" />
              חזרה לדשבורד הבית
            </Link>
          </Button>
        </div>
      </main>
    </div>
  );
}
